import { ManagePatientRepository } from '@/io/repository/ManagePatientRepository';
import { ObservationsRepository } from '@/io/repository/ObservationsRepository';
import { UploadFileRepository } from '@/io/repository/UploadFileRepository';
import { CreatePatientModel } from '@/models/CreatePatientModel';
import { EditTherapeuticObjectivesData } from '@/models/EditTherapeuticObjectivesModel';
import { UploadFileModel } from '@/models/FileModel.ts';
import { RenewCarePlanData } from '@/models/RenewCarePlanModel';
import { ValidateOrRejectIdentity } from '@/models/identity/FrenchIdentity';
import { ContactData } from '@/pages/patient-medical-file/contact-patient/ContactPatient.schema';
import { Queries, makeQueryKey } from '@/queries/Queries';
import { createMutation } from '@/queries/utils/CreateMutation';
import { createQuery } from '@/queries/utils/CreateQuery';
import { createSuspenseQuery } from '@/queries/utils/CreateSuspenseQuery.ts';

export class PractitionerQueries {
  constructor(
    private readonly managePatient: ManagePatientRepository = new ManagePatientRepository(),
    private readonly observation: ObservationsRepository = new ObservationsRepository(),
    private readonly uploadFile: UploadFileRepository = new UploadFileRepository(),
  ) {}

  /*******************************/
  /*********** QUERIES ***********/
  /*******************************/

  usePatientsList = createQuery(
    'practitioner-patients',
    ({}) => [],
    async () => this.managePatient.getPatientList(),
    {
      staleTime: 30_000,
    },
  );

  usePatient = createSuspenseQuery(
    'observed-patient',
    patientId => [patientId],
    async (patientId: string) => this.managePatient.getPatient(patientId),
    {
      staleTime: 30_000,
    },
  );

  useIdentitiesToValidate = createQuery(
    'identities-to-validate',
    ({}) => [],
    async () => this.managePatient.getIdentitiesToValidate(),
  );

  useNurseNotice = createQuery(
    'nurse-notice',
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.observation.getNurseNotice(patientId),
  );

  useCareTeam = createQuery(
    'careTeam',
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.managePatient.getCareTeam(patientId),
  );

  useCarePlans = createSuspenseQuery(
    'carePlans',
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.managePatient.getCarePlans(patientId),
  );

  useCarePlanDirectives = createSuspenseQuery(
    'carePlanDirectives',
    ({ patientId }) => makeQueryKey(patientId),
    async (data: { patientId?: string }) =>
      this.managePatient.getCarePlanDirectives(data.patientId),
  );

  useListContacts = createQuery(
    'patient-contacts',
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.managePatient.getListContacts(patientId),
  );

  useDiabetesDisplayMode = createQuery(
    'glycemia-display-mode',
    patientId => makeQueryKey(patientId),
    async (patientId: string) =>
      this.managePatient.getDiabetesDisplayMode(patientId),
  );

  useHSPList = createSuspenseQuery(
    'practitioner-hsp',
    ({}) => [],
    async () => this.managePatient.listHSP(),
  );

  usePatientEvents = createSuspenseQuery(
    'patient-events',
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.managePatient.getPatientEvents(patientId),
  );

  /*******************************/
  /********** MUTATIONS **********/
  /*******************************/

  useCreatePatient = createMutation(
    'create-patient',
    async (data: CreatePatientModel) => this.managePatient.createPatient(data),
    {
      onSuccess: async (_, __, { queryClient }) => {
        this.usePatientsList.invalidate(queryClient, {});
      },
    },
  );

  useRenewCarePlan = createMutation(
    'renew-care-plan',
    async ({
      patientId,
      data,
    }: {
      patientId: string;
      data: RenewCarePlanData;
    }) => this.managePatient.renewCarePlan(patientId, data),
    {
      onSuccess: async (_, { patientId }, { queryClient }) => {
        this.useCarePlans.clear(queryClient, patientId);
        this.usePatientsList.invalidate(queryClient, {});
        Queries.condition.useCondition.invalidate(queryClient, patientId);
        this.usePatient.invalidate(queryClient, patientId);
        this.useShouldNudgeRenewal.invalidate(queryClient, patientId);
        this.usePatientEvents.invalidate(queryClient, patientId);
        setTimeout(() => {
          this.useCarePlanDirectives.clear(queryClient, { patientId });
        }, 1000);
      },
    },
  );

  useEditTherapeuticObjectives = createMutation(
    'edit-therapeutic-objectives',
    async ({
      carePlanId,
      data,
    }: {
      carePlanId: string;
      data: EditTherapeuticObjectivesData;
    }) => this.managePatient.editTherapeuticObjectives(carePlanId, data),
    {
      onSuccess: async (data, {}, { queryClient }) => {
        this.useCarePlans.invalidate(queryClient, data.id);
      },
    },
  );

  useValidateOrRejectIdentity = createMutation(
    'validate-or-reject-identity',
    async ({
      patientId,
      action,
    }: {
      patientId: string;
      action: ValidateOrRejectIdentity;
    }) => this.managePatient.validateOrRejectIdentity(patientId, action),
    {
      onSuccess: async (_, { patientId }, { queryClient }) => {
        this.useIdentitiesToValidate.invalidate(queryClient, {});
        this.usePatient.invalidate(queryClient, patientId);
      },
    },
  );

  useCreateContact = createMutation(
    'create-contact',
    async ({ patientId, data }: { patientId: string; data: ContactData }) =>
      this.managePatient.createPatientContacts(patientId, data),
    {
      onSuccess: (_, { patientId }, { queryClient }) => {
        this.useListContacts.invalidate(queryClient, patientId);
      },
    },
  );

  useUpdateContact = createMutation(
    'update-contact',
    async ({
      patientId,
      contactId,
      data,
    }: {
      patientId: string;
      contactId: string;
      data: Partial<ContactData>;
    }) => this.managePatient.updatePatientContacts(patientId, contactId, data),
    {
      onSuccess: (_, { patientId }, { queryClient }) => {
        this.useListContacts.invalidate(queryClient, patientId);
      },
    },
  );

  useDeleteContact = createMutation(
    'delete-contact',
    async ({
      patientId,
      contactId,
    }: {
      patientId: string;
      contactId: string;
    }) => this.managePatient.deletePatientContacts(patientId, contactId),
    {
      onSuccess: (_, { patientId }, { queryClient }) => {
        this.useListContacts.invalidate(queryClient, patientId);
      },
    },
  );

  useUploadFile = createMutation('upload-file', async (data: UploadFileModel) =>
    this.uploadFile.uploadFile(data),
  );

  useDeleteNurseReinclusionSuggestion = createMutation(
    'delete-nurse-reinclusion-suggestion',
    async (patientId: string) =>
      this.managePatient.deleteNurseReinclusionSuggestion(patientId),
    {
      onSuccess: (_, patientId, { queryClient }) => {
        this.usePatientsList.invalidate(queryClient, {});
        this.usePatient.invalidate(queryClient, patientId);
        this.useCarePlans.invalidate(queryClient, patientId);
        this.useGetNurseReinclusionSuggestion.invalidate(
          queryClient,
          patientId,
        );
      },
    },
  );

  useCreateNurseReinclusionSuggestion = createMutation(
    'create-nurse-reinclusion-suggestion',
    async (patientId: string) =>
      this.managePatient.createNurseReinclusionSuggestion(patientId),
    {
      onSuccess: async (_, patientId, { queryClient }) => {
        this.usePatientsList.invalidate(queryClient, {});
        this.usePatient.invalidate(queryClient, patientId);
        this.useCarePlans.invalidate(queryClient, patientId);
        this.useGetNurseReinclusionSuggestion.invalidate(
          queryClient,
          patientId,
        );
      },
    },
  );

  useGetNurseReinclusionSuggestion = createQuery(
    'get-nurse-reinclusion-suggestion',
    patientId => makeQueryKey(patientId),
    async (patientId: string) =>
      this.managePatient.getNurseReinclusionSuggestion(patientId),
  );

  useShouldNudgeRenewal = createSuspenseQuery(
    'should-nudge-renewal',
    patientId => makeQueryKey(patientId),
    async (patientId: string) => this.managePatient.getRenewalNudge(patientId),
  );

  useRefuseToRenew = createMutation(
    'refuse-to-renew',
    async (patientId: string) => this.managePatient.refuseRenewal(patientId),
    {
      onSuccess: (_, patientId, { queryClient }) => {
        this.useShouldNudgeRenewal.invalidate(queryClient, patientId);
        this.usePatientEvents.invalidate(queryClient, patientId);
      },
    },
  );
}
